import React, { useState } from 'react';
import { Calendar, dateFnsLocalizer, Views } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import addMonths from 'date-fns/addMonths';
import addWeeks from 'date-fns/addWeeks';
import addDays from 'date-fns/addDays';
import subMonths from 'date-fns/subMonths';
import subWeeks from 'date-fns/subWeeks';
import subDays from 'date-fns/subDays';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { styled as muiStyled } from '@mui/material/styles';
import { 
  Button, 
  ButtonGroup as MuiButtonGroup, 
  IconButton,
  Typography,
  useTheme,
  useMediaQuery,
  Stack,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import { Meeting } from '../types/meetings';
import { Box } from '@mui/material';

const locales = {
  'en-US': require('date-fns/locale/en-US')
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

interface MeetingsCalendarProps {
  meetings: Meeting[];
  currentUser?: string;
  onSelectMeeting?: (meeting: Meeting) => void;
  onSelectSlot?: (slotInfo: { start: Date; end: Date }) => void;
  availableSlots?: { start: Date; end: Date }[];
}

const MeetingsCalendar: React.FC<MeetingsCalendarProps> = ({
  meetings,
  currentUser,
  onSelectMeeting,
  onSelectSlot,
  availableSlots = []
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [view, setView] = useState<'month' | 'week' | 'day'>(isMobile ? Views.DAY : Views.MONTH);
  const [date, setDate] = useState(new Date());

  const handleViewChange = (newView: string) => {
    setView(newView as 'month' | 'week' | 'day');
  };

  const navigateCalendar = (action: 'PREV' | 'NEXT' | 'TODAY') => {
    switch (action) {
      case 'PREV':
        if (view === 'month') {
          setDate(subMonths(date, 1));
        } else if (view === 'week') {
          setDate(subWeeks(date, 1));
        } else if (view === 'day') {
          setDate(subDays(date, 1));
        }
        break;
      case 'NEXT':
        if (view === 'month') {
          setDate(addMonths(date, 1));
        } else if (view === 'week') {
          setDate(addWeeks(date, 1));
        } else if (view === 'day') {
          setDate(addDays(date, 1));
        }
        break;
      case 'TODAY':
        setDate(new Date());
        break;
    }
  };

  const eventStyleGetter = (event: Meeting) => {
    const isMyMeeting = event.owner === currentUser;
    
    return {
      style: {
        backgroundColor: isMyMeeting ? '#3182ce' : '#805AD5',
        borderColor: isMyMeeting ? '#2C5282' : '#6B46C1',
        color: '#FFFFFF',
        borderRadius: '4px',
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        fontSize: isMobile ? '12px' : '14px',
        padding: '2px 6px',
      }
    };
  };

  const slotPropGetter = (date: Date) => {
    const isAvailable = availableSlots.some(slot => 
      slot.start.getTime() <= date.getTime() && 
      date.getTime() < slot.end.getTime()
    );

    return {
      style: {
        backgroundColor: isAvailable ? '#f0fff4' : undefined,
        cursor: isAvailable ? 'pointer' : 'not-allowed',
      }
    };
  };

  const handleSelectSlot = (slotInfo: { start: Date; end: Date }) => {
    const isAvailable = availableSlots.some(slot => 
      slot.start.getTime() <= slotInfo.start.getTime() && 
      slotInfo.start.getTime() < slot.end.getTime()
    );

    if (isAvailable && onSelectSlot) {
      onSelectSlot(slotInfo);
    }
  };

  return (
    <CalendarContainer>
      <Stack spacing={2} mb={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <MuiButtonGroup size={isMobile ? "small" : "medium"}>
            <IconButton onClick={() => navigateCalendar('PREV')} size={isMobile ? "small" : "medium"}>
              <ChevronLeftIcon />
            </IconButton>
            <IconButton onClick={() => navigateCalendar('TODAY')} size={isMobile ? "small" : "medium"}>
              <TodayIcon />
            </IconButton>
            <IconButton onClick={() => navigateCalendar('NEXT')} size={isMobile ? "small" : "medium"}>
              <ChevronRightIcon />
            </IconButton>
          </MuiButtonGroup>

          <Typography variant={isMobile ? "subtitle1" : "h6"}>
            {format(date, 'MMMM yyyy')}
          </Typography>
        </Box>

        <Box display="flex" justifyContent="center">
          <MuiButtonGroup size={isMobile ? "small" : "medium"}>
            <Button
              variant={view === 'month' ? 'contained' : 'outlined'}
              onClick={() => handleViewChange('month')}
              // sx={{ display: isMobile ? 'none' : 'inline-flex' }}
            >
              Month
            </Button>
            <Button
              variant={view === 'week' ? 'contained' : 'outlined'}
              onClick={() => handleViewChange('week')}
            >
              Week
            </Button>
            <Button
              variant={view === 'day' ? 'contained' : 'outlined'}
              onClick={() => handleViewChange('day')}
            >
              Day
            </Button>
          </MuiButtonGroup>
        </Box>
      </Stack>

      <Calendar
        localizer={localizer}
        events={meetings}
        startAccessor="start"
        endAccessor="end"
        style={{ height: isMobile ? 'calc(100vh - 200px)' : 600, padding: 0 }}
        view={view}
        onView={handleViewChange}
        date={date}
        onNavigate={(newDate) => setDate(newDate)}
        eventPropGetter={eventStyleGetter}
        slotPropGetter={slotPropGetter}
        onSelectEvent={onSelectMeeting}
        onSelectSlot={handleSelectSlot}
        selectable
        popup
        step={30}
        timeslots={1}
        defaultView={isMobile ? Views.DAY : Views.MONTH}
        toolbar={false}
        formats={{
          timeGutterFormat: isMobile ? 'ha' : 'h:mm a',
          selectRangeFormat: ({ start, end }) =>
            `${format(start, 'h:mm a')} - ${format(end, 'h:mm a')}`,
          eventTimeRangeFormat: ({ start, end }) =>
            `${format(start, 'h:mm a')} - ${format(end, 'h:mm a')}`,
        }}
      />
    </CalendarContainer>
  );
};

const CalendarContainer = muiStyled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  
  '& .rbc-calendar': {
    background: 'white',
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },

  '& .rbc-time-view': {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },

  '& .rbc-time-content': {
    border: 'none',
  },

  '& .rbc-time-gutter .rbc-timeslot-group': {
    minHeight: '60px',
  },

  '& .rbc-timeslot-group': {
    minHeight: '60px',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },

  '& .rbc-time-slot': {
    minHeight: '60px',
  },

  '& .rbc-time-header-content': {
    borderLeft: 'none',
  },

  '& .rbc-header': {
    padding: theme.spacing(1),
    fontWeight: 600,
    textTransform: 'uppercase',
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
  },

  '& .rbc-time-header': {
    backgroundColor: theme.palette.background.paper,
    borderTopLeftRadius: theme.shape.borderRadius,
    borderTopRightRadius: theme.shape.borderRadius,
  },

  '& .rbc-time-content > * + * > *': {
    borderLeft: `1px solid ${theme.palette.divider}`,
  },

  '& .rbc-today': {
    backgroundColor: theme.palette.action.hover,
  },

  [theme.breakpoints.down('sm')]: {
    '& .rbc-toolbar': {
      flexDirection: 'column',
      gap: theme.spacing(1),
    },

    '& .rbc-time-gutter .rbc-timeslot-group': {
      minHeight: '50px',
    },

    '& .rbc-timeslot-group': {
      minHeight: '50px',
    },

    '& .rbc-time-slot': {
      minHeight: '50px',
    },

    '& .rbc-time-gutter': {
      fontSize: '0.75rem',
      width: '48px',
    },

    '& .rbc-time-header-content': {
      fontSize: '0.75rem',
    },

    '& .rbc-event': {
      padding: '2px 4px',
    },
  },
}));

export default MeetingsCalendar;
